import React, { useState, useEffect, useRef } from 'react';
import NavMenu from './NavMenu';

function SplitText({
  text,
  totalDuration = 750,
  startOffset = 0,
  trigger = false,
  style = {},
}) {
  const lettersRef = useRef([]);

  const withMarkers = text.replace(/\n/g, ' [[BR]] ');
  const rawChunks = withMarkers.split(/\s+/).filter(Boolean);

  const chunks = rawChunks.map((chunk) =>
    chunk === '[[BR]]'
      ? { type: 'break' }
      : { type: 'word', letters: [...chunk] }
  );

  let totalLetters = 0;
  chunks.forEach((c) => {
    if (c.type === 'word') {
      totalLetters += c.letters.length;
    }
  });

  useEffect(() => {
    if (!trigger) return;

    requestAnimationFrame(() => {
      let letterIndex = 0;
      lettersRef.current.forEach((el) => {
        if (el) {
          const fraction =
            totalLetters > 1 ? letterIndex / (totalLetters - 1) : 0;
          const delayMs = startOffset + fraction * totalDuration;
          el.style.transition = `opacity 0.4s ease ${delayMs}ms`;
          el.style.opacity = 1;
          letterIndex++;
        }
      });
    });
  }, [trigger, totalLetters, totalDuration, startOffset]);

  let globalIndex = 0;

  return (
    <span style={{ display: 'inline-block', ...style }}>
      {chunks.map((chunk, i) => {
        if (chunk.type === 'break') {
          return <br key={`br-${i}`} />;
        }
        return (
          <span
            key={`word-${i}`}
            style={{
              display: 'inline-block',
              whiteSpace: 'normal',
              marginRight: '0.4em',
            }}
          >
            {chunk.letters.map((char) => {
              const current = globalIndex++;
              return (
                <span
                  key={`letter-${current}`}
                  ref={(el) => (lettersRef.current[current] = el)}
                  style={{
                    display: 'inline-block',
                    opacity: 0,
                  }}
                >
                  {char}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

function App() {
  const [bgVisible, setBgVisible] = useState(false);
  const [textTriggered, setTextTriggered] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [blackOverlayVisible, setBlackOverlayVisible] = useState(true);

  useEffect(() => {
    const delay = 250;

    setTimeout(() => {
      setBgVisible(true);
    }, delay);

    setTimeout(() => {
      setBlackOverlayVisible(false);
    }, delay);

    const t1 = setTimeout(() => {
      setTextTriggered(true);
    }, 500 + delay);

    const t2 = setTimeout(() => {
      setButtonsVisible(true);
    }, 1500 + delay + 600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const containerStyle = {
    position: 'relative',
    width: '100vw',
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: '"Times New Roman", Times, serif',
    color: 'white',
    padding: '12px',
    boxSizing: 'border-box',
    margin: 0,
    overflow: 'hidden',
  };

  const iframeStyle = {
    position: 'absolute',
    top: '-10%',
    left: '-10%',
    width: '120%',
    height: '120%',
    zIndex: -2,
    pointerEvents: 'none', // Prevent user interaction with the video
  };

  const blackOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    zIndex: 0,
    transition: 'opacity 1000ms ease',
    opacity: blackOverlayVisible ? 1 : 0,
  };

  const headerStyle = {
    margin: '10px 20px',
    fontSize: '1.6rem',
    letterSpacing: '2px',
    textTransform: 'none',
    textAlign: 'center',
    fontWeight: '400',
    position: 'relative',
    zIndex: 1,
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: '5px',
    transition: 'opacity 250ms ease, transform 250ms ease',
    opacity: buttonsVisible ? 1 : 0,
    transform: buttonsVisible ? 'translateY(0)' : 'translateY(10px)',
    position: 'relative',
    zIndex: 1,
  };

  const footerTextStyle = {
    fontSize: '1.3rem',
    textAlign: 'center',
    letterSpacing: '2px',
    lineHeight: '1.8',
    fontWeight: '400',
    display: 'inline-block',
    marginBottom: '1rem',
    position: 'relative',
    zIndex: 1,
  };

  const bottomButtonContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto',
    transition: 'opacity 500ms ease, transform 500ms ease',
    opacity: buttonsVisible ? 1 : 0,
    transform: buttonsVisible ? 'translateY(0)' : 'translateY(10px)',
    position: 'relative',
    zIndex: 1,
  };

  return (
    <div style={containerStyle}>
      {/* Embedded YouTube video as background */}
      <iframe
  style={{
    ...iframeStyle,
    filter: 'brightness(80%)',
  }}
  src="https://www.youtube.com/embed/HSCyF-fihG0?autoplay=1&loop=1&mute=1&playlist=HSCyF-fihG0&controls=0&rel=0&modestbranding=1&playsinline=1"
  frameBorder="0"
  allow="autoplay; loop; muted"
  allowFullScreen
  title="Background Video"
  preload="auto"
/>

      {/* Black overlay */}
      <div style={blackOverlayStyle} />

      <NavMenu />

      <div style={headerStyle}>
        <SplitText
          text="Sushi Junai Omakase x Sun Consulting"
          totalDuration={1000}
          startOffset={250}
          trigger={textTriggered}
        />
      </div>

      <div className="button-container" style={buttonContainerStyle}>
        <button className="header" onClick={() => window.open("https://maps.app.goo.gl/9dBPdf2CPd4t6BZ98", "_blank")}>315 Congress Ave</button>
        <button className="header">Book Your Table Here</button>
      </div>

      <div style={{ marginTop: 'auto', textAlign: 'center' }}>
        <div style={footerTextStyle}>
          <SplitText
            text={
              'WE ARE SUSHI JUNAI OMAKASE\n a creative culinary branch focused on creating unique and ephemeral culinary experiences'
            }
            totalDuration={1000}
            startOffset={250}
            trigger={textTriggered}
          />
        </div>
        <div className="button-container" style={bottomButtonContainerStyle}>
          <button id="footer">View unique experiences</button>
        </div>
      </div>
    </div>
  );
}

export default App;